import type { MessageDescriptor } from 'react-intl';

import { BBAN_BANK_CODE_REGEX, BBAN_BRANCH_CODE_REGEX, BBAN_ACCOUNT_NUMBER_REGEX } from '@/lib/bban';

export interface FieldValidation {
  minLength?: number;
  maxLength?: number;
  patterns?: { pattern: RegExp; message: MessageDescriptor }[];
}

export interface FieldConfig {
  span?: number;
  validation?: FieldValidation;
}

export const BENEFICIARY_FIELD_CONFIG: Record<string, FieldConfig> = {
  city: { span: 6 },
  state: { span: 6 },
  postcode: {
    span: 6,
    validation: { maxLength: 10 },
  },
  // The account is entered as 3 parts: bank/branch codes accept 1–3 digits and
  // the account number 1–13 digits; each entered value is zero-padded to its
  // fixed width (3/3/13) and the parts are assembled into an Israeli IBAN sent
  // to the backend as `account_number` (see @/lib/iban / @/lib/bban).
  bank_code: {
    span: 6,
    validation: {
      patterns: [
        {
          pattern: BBAN_BANK_CODE_REGEX,
          message: { id: 'validation.bankCode', defaultMessage: 'Bank code must be 1–3 digits' },
        },
      ],
    },
  },
  branch_code: {
    span: 6,
    validation: {
      patterns: [
        {
          pattern: BBAN_BRANCH_CODE_REGEX,
          message: { id: 'validation.branchCode', defaultMessage: 'Branch code must be 1–3 digits' },
        },
      ],
    },
  },
  // Third IBAN part: the IBAN preview wraps bank/branch/account as
  // IL + check digits + zero-padded BBAN.
  bank_account_number: {
    validation: {
      patterns: [
        {
          pattern: BBAN_ACCOUNT_NUMBER_REGEX,
          message: { id: 'validation.bbanAccountNumber', defaultMessage: 'Account number must be 1–13 digits' },
        },
      ],
    },
  },
  account_routing_number: {
    span: 6,
    validation: {
      patterns: [
        {
          pattern: /^[0-9]+$/,
          message: { id: 'beneficiary.validation.onlyDigits', defaultMessage: 'Only digits allowed' },
        },
        {
          pattern: /^[0-9]{9}$/,
          message: { id: 'beneficiary.validation.mustBe9Digits', defaultMessage: 'Must be exactly 9 digits' },
        },
      ],
    },
  },
  // account_currency: full width (no span override → defaults to 12).
};
